<template>
  <div class="mt-2">
    <DividerWithText text="Details for dimension and sectors per aggregation level" color="#abc" text-color="#479"/>
    <v-table class="mt-4" hover>
      <colgroup>
        <col />
        <col />
      </colgroup>
      <colgroup>
        <col />
        <col />
        <col />
      </colgroup>
      <thead>
      <tr>
        <th>Name</th>
        <th>Dimension/Sector</th>
        <th>Aggregation Level</th>
        <th>Weight</th>
      </tr>
      </thead>
      <tbody>
      <tr
          v-for="(c, index) in combinations"
          :key="`${c.level}_${c.name}`"
          :class="getColor(c.type)"
      >
        <td
            :class="getColor(c.type)"
            :rowspan="rowsPerLevel"
            v-if="index % rowsPerLevel === 0"
            class="border-e-sm"
        >
          <b>{{ StringManipulation.methods.clearAndCapitalize(c.name) }}</b>
        </td>
        <td :rowspan="rowsPerLevel" v-if="index % rowsPerLevel === 0"
            class="border-e-sm"
        >
          {{ StringManipulation.methods.capitalize(c.type) }}
        </td>
        <td class="border-e-sm">
          {{ StringManipulation.methods.clearAndCapitalize(c.level) }}</td>
        <td @dblclick="startEditing(index, c.weight)">
          <div v-if="editing.index === index" class="d-flex align-center">
            <v-text-field
                v-model="editableWeight"
                @keydown.enter.prevent="!isWeightInvalid && stopEditing(c)"
                @keydown.esc.prevent="cancelEditing"
                autofocus
                density="compact"
                hide-details
                :error-messages="isWeightInvalid ? 'Weight must be between 0 and 1' : ''"
            ></v-text-field>
            <v-btn icon="fas fa-check" size="small" variant="text" color="green" @click="stopEditing(c)" :disabled="isWeightInvalid"></v-btn>
            <v-btn icon="fas fa-times" size="small" variant="text" color="red" @click="cancelEditing"></v-btn>
          </div>
          <span v-else>{{ c.weight }}</span>
        </td>
      </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script setup>
import StringManipulation from "@/Mixins/StringManipulation";
import {computed, ref} from "vue";
import DividerWithText from "@/Shared/DividerWithText.vue";
import { router } from "@inertiajs/vue3";
import * as $routes from "@/packs/routes";

const props = defineProps({
  levels: {
    type: Array,
    required: true,
  },
  sectors: {
    type: Array,
    required: true,
  },
  dimension: {
    type: Object,
    required: true,
  },
  projectId: {
    type: Number,
    required: true
  },
  kpiId: {
    type: Number,
    required: true
  }
});

const editing = ref({ index: null, oldValue: null });
const editableWeight = ref(null);

function startEditing(index, weight) {
  editing.value = { index: index, oldValue: weight };
  editableWeight.value = weight;
}

function stopEditing(item) {
  if (editing.value.oldValue !== editableWeight.value) {
    saveWeights(item, editableWeight.value);
  }
  editing.value = { index: null, oldValue: null };
}

function cancelEditing() {
  editing.value = { index: null, oldValue: null };
}

function saveWeights(item, newWeight) {
  let payload;
  if (item.type === 'dimension') {
    payload = {
      dimension_attributes: {
        id: item.id,
        aggregation_level_results_attributes: [{
          id: item.agg_id,
          weight: newWeight
        }]
      }
    };
  } else {
    payload = {
      sectors_attributes: [{
        id: item.id,
        kpi_id: props.kpiId,
        aggregation_level_results_attributes: [{
          id: item.agg_id,
          weight: newWeight
        }]
      }]
    };
  }

  router.put($routes.project_kpi(props.projectId, props.kpiId), {kpi: payload});
}

const isWeightInvalid = computed(() => {
  if (editing.value.index === null) return false;

  const newWeight = parseFloat(editableWeight.value);
  if (isNaN(newWeight) || newWeight < 0 || newWeight > 1) return true;

  return false;
});

const combinations = computed(() => {
  const levels = props.levels;
  const sectors = props.sectors;
  const dimension = props.dimension;
  let arr = [];

  for (let i = 0; i < sectors.length; i++) {
    const sector = sectors[i];
    for (let l = 0; l < sector.agg.length; l++) {
      const level = sector.agg[l];
      if (levels.includes(level.name)) {
        arr.push({
          id: level.sector_id,
          agg_id: level.id,
          level: level.name,
          type: "sector",
          name: sector.name,
          value: level.value,
          weight: level.weight == null ? "-" : level.weight,
        });
      }
    }
  }
  arr = arr.sort((a, b) => (a.name > b.name ? 1 : -1));
  // add dimension combinations
  for (let l = 0; l < dimension.agg.length; l++) {
    const level = dimension.agg[l];
    if (levels.includes(level.name)) {
      arr.unshift({
        id: level.dimension_id,
        agg_id: level.id,
        level: level.name,
        type: "dimension",
        name: dimension.name,
        value: level.value,
        weight: level.weight,
      });
    }
  }

  return arr;
});

const rowsPerLevel = computed(() => {
  return props.levels.length;
});

function getColor(type) {
  if (type === "dimension") return "primary";
  else return "success";
}
</script>

<style scoped>
.primary {
  background-color: #cfe2ff;
  color: black;
}

.success {
  background-color: #d1e7dd; /* Example success color */
  color: black;
}

::v-deep thead tr th {
  background-color: #4a5876;
  font-weight: bold;
}

::v-deep thead tr {
  color: #fff !important;
}
.hover-cell {
  transition: background-color 0.3s; /* Smooth transition */
}

.hover-cell:hover {
  background-color: rgba(100, 100, 100, 0.1); /* Adjust hover color */
}
</style>